'use client';

import { useState, useCallback } from 'react';
import { toast } from 'sonner';

export interface OperationState<T = unknown> {
  loading: boolean;
  error: string | null;
  success: boolean;
  data: T;
}

export interface OperationOptions {
  successMessage?: string;
  errorMessage?: string;
  showToast?: boolean;
  resetAfter?: number; // 自动重置状态的时间（毫秒）
}

export function useOperationState<T = unknown>(initialData: T | null = null) {
  const [state, setState] = useState<OperationState<T | null>>({
    loading: false,
    error: null,
    success: false,
    data: initialData,
  });

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({
      ...prev,
      loading,
      error: loading ? null : prev.error, // 开始加载时清除错误
    }));
  }, []);

  const setError = useCallback((error: string | null, showToast = true) => {
    setState(prev => ({
      ...prev,
      loading: false,
      error,
      success: false,
    }));
    
    if (error && showToast) {
      toast.error(error);
    }
  }, []);

  const setSuccess = useCallback((data: T | null = null, message?: string, showToast = true) => {
    setState(prev => ({
      ...prev,
      loading: false,
      error: null,
      success: true,
      data: data !== null ? data : prev.data,
    }));
    
    if (message && showToast) {
      toast.success(message);
    }
  }, []);

  const reset = useCallback(() => {
    setState({
      loading: false,
      error: null,
      success: false,
      data: initialData,
    });
  }, [initialData]);

  const execute = useCallback(async <R>(
    operation: () => Promise<R>,
    options: OperationOptions = {}
  ): Promise<R | null> => {
    const {
      successMessage,
      errorMessage = '操作失败',
      showToast = true,
      resetAfter,
    } = options;

    try {
      setLoading(true);
      const result = await operation();
      setSuccess(result as T | null, successMessage, showToast);
      
      if (resetAfter) {
        setTimeout(reset, resetAfter);
      }
      
      return result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string } | undefined;
      const message = err?.response?.data?.message || err?.message || errorMessage;
      setError(message, showToast);
      return null;
    }
  }, [setLoading, setSuccess, setError, reset]);

  return {
    ...state,
    setLoading,
    setError,
    setSuccess,
    reset,
    execute,
  };
}

// 专门用于表单操作的Hook
export function useFormOperation<T = unknown>(initialData: T | null = null) {
  const operation = useOperationState(initialData);
  
  const submitForm = useCallback(async <R>(
    formOperation: () => Promise<R>,
    options: OperationOptions & {
      resetForm?: () => void;
    } = {}
  ) => {
    const { resetForm, ...operationOptions } = options;
    
    const result = await operation.execute(formOperation, operationOptions);
    
    if (result && resetForm) {
      resetForm();
    }
    
    return result;
  }, [operation]);

  return {
    ...operation,
    submitForm,
  };
}

// 专门用于数据获取的Hook
export function useDataFetch<T>(initialData: T | null = null) {
  const operation = useOperationState(initialData);
  
  const fetchData = useCallback(async (
    fetchOperation: () => Promise<T>,
    options: Omit<OperationOptions, 'successMessage'> & {
      silent?: boolean; // 静默获取，不显示错误toast
    } = {}
  ) => {
    const { silent = false, ...operationOptions } = options;
    
    return operation.execute<T>(fetchOperation, {
      ...operationOptions,
      showToast: !silent,
    });
  }, [operation]);

  return {
    ...operation,
    fetchData,
    refetch: (fetchOperation: () => Promise<T>) => fetchData(fetchOperation, { silent: true }),
  };
}